/**
 * 搜索筛选条（牛只市场左侧面板）
 * 受控组件：value 变化即回调 onChange（父级 300ms 防抖）
 */
import { BREEDS, REGIONS, TRADE_MODE } from '@/utils/constants';

export interface FilterValue {
  breed?: string;
  gender?: number;
  min_weight?: number;
  max_weight?: number;
  min_price?: number;
  max_price?: number;
  region?: string;
  trade_mode?: number;
  quarantine?: number;
}

interface FilterBarProps {
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  onClear: () => void;
}

function num(v: string | undefined): number | undefined {
  if (v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

export default function FilterBar({ value, onChange, onClear }: FilterBarProps) {
  const hasFilter =
    value.breed ||
    value.min_weight ||
    value.max_weight ||
    value.min_price ||
    value.max_price ||
    value.region ||
    value.trade_mode ||
    value.quarantine;

  return (
    <div className="filter-bar">
      <div className="filter-bar__head">
        <span className="filter-bar__title">筛选</span>
        {hasFilter ? (
          <button className="filter-bar__clear" onClick={onClear}>清除筛选</button>
        ) : null}
      </div>

      <div className="filter-group">
        <div className="filter-label">品种</div>
        <select
          className="form-control"
          value={value.breed ?? ''}
          onChange={(e) => onChange({ ...value, breed: e.target.value || undefined })}
        >
          <option value="">全部品种</option>
          {BREEDS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <div className="filter-label">性别</div>
        <div className="filter-check">
          {[0, 1].map((g) => (
            <label className="form-check" key={g}>
              <input
                type="radio"
                name="gender"
                checked={value.gender === g}
                onChange={() => onChange({ ...value, gender: g })}
              />
              {g === 0 ? '母牛' : '公牛'}
            </label>
          ))}
        </div>
      </div>

      <div className="filter-group">
        <div className="filter-label">体重区间（kg）</div>
        <div className="filter-range">
          <input
            className="form-control"
            type="number"
            placeholder="最小"
            min={0}
            value={value.min_weight ?? ''}
            onChange={(e) => onChange({ ...value, min_weight: num(e.target.value) })}
          />
          <span>—</span>
          <input
            className="form-control"
            type="number"
            placeholder="最大"
            min={0}
            value={value.max_weight ?? ''}
            onChange={(e) => onChange({ ...value, max_weight: num(e.target.value) })}
          />
        </div>
      </div>

      <div className="filter-group">
        <div className="filter-label">价格区间（元/头）</div>
        <div className="filter-range">
          <input
            className="form-control"
            type="number"
            placeholder="最低"
            min={0}
            value={value.min_price ?? ''}
            onChange={(e) => onChange({ ...value, min_price: num(e.target.value) })}
          />
          <span>—</span>
          <input
            className="form-control"
            type="number"
            placeholder="最高"
            min={0}
            value={value.max_price ?? ''}
            onChange={(e) => onChange({ ...value, max_price: num(e.target.value) })}
          />
        </div>
      </div>

      <div className="filter-group">
        <div className="filter-label">地区</div>
        <select
          className="form-control"
          value={value.region ?? ''}
          onChange={(e) => onChange({ ...value, region: e.target.value || undefined })}
        >
          <option value="">全部地区</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <div className="filter-label">交易方式</div>
        <div className="filter-check">
          {[1, 2, 3].map((mode) => (
            <label className="form-check" key={mode}>
              <input
                type="radio"
                name="trade_mode"
                checked={value.trade_mode === mode}
                onChange={() => onChange({ ...value, trade_mode: mode })}
              />
              {TRADE_MODE[mode].label}
            </label>
          ))}
        </div>
      </div>

      <div className="filter-group">
        <div className="filter-label">检疫状态</div>
        <label className="form-check">
          <input
            type="checkbox"
            checked={value.quarantine === 1}
            onChange={(e) => onChange({ ...value, quarantine: e.target.checked ? 1 : undefined })}
          />
          已检疫 / 检疫合格
        </label>
      </div>
    </div>
  );
}
